class CartDrawerSection extends HTMLElement {
  cartUpdateUnsubscriber = undefined;
  constructor() {
    super();

    this.cartType = this.dataset.cartType;
    this.drawerClass = "wt-cart__drawer";
    this.drawer = this.querySelector(`.${this.drawerClass}`);
    this.classDrawerActive = `${this.drawerClass}--open`;
    this.pageOverlayClass = "page-overlay-cart";
    this.activeOverlayBodyClass = `${this.pageOverlayClass}-on`;
    this.body = document.body;
    this.triggerQuery = [
      `.wt-cart__trigger`,
      `.wt-cart__back-link`,
      `.${this.pageOverlayClass}`,
    ].join(", ");
    this.triggers = () => document.querySelectorAll(this.triggerQuery);
    this.isOpen = false;
    this.isCartPage = window.location.pathname === window.routes.cart_url;
    this.closeButton = () => this.querySelector(".wt-cart__drawer__close");
    this.mainTrigger = document.querySelector(".wt-cart__trigger");
    this.toggleEelements = () =>
      this.querySelectorAll(this.dataset.toggleTabindex);
  }

  connectedCallback() {
    document.addEventListener("cart-drawer:refresh", (e) =>
      this.refreshCartDrawer(e),
    );

    if (this.isCartPage) {
      return;
    }

    this.init();
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      if (this.isOpen) {
        setTabindex(this.toggleEelements(), "0");
        this.closeButton().focus();
      }
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  getFocusableElements() {
    const focusableElementsSelector =
      "button, [href], input, select, [tabindex]";
    const focusableElements = () =>
      Array.from(this.querySelectorAll(focusableElementsSelector)).filter(
        (el) => !el.hasAttribute("disabled") && el.tabIndex >= 0,
      );

    return {
      focusableElements,
      first: focusableElements()[0],
      last: focusableElements()[focusableElements().length - 1],
    };
  }

  temporaryHideFocusVisible() {
    document.body.classList.add("no-focus-visible");
  }

  onToggle() {
    if (this.hasAttribute("open")) {
      this.removeAttribute("open");
      this.isOpen = false;
      // this.mainTrigger.focus();
      this.temporaryHideFocusVisible();
      setTabindex(this.toggleEelements(), "-1");
    } else {
      this.setAttribute("open", "");
      this.isOpen = true;
      this.closeButton().focus();
      this.temporaryHideFocusVisible();
      setTabindex(this.toggleEelements(), "0");
    }
  }

  ensureDrawerOpen() {
    if (!this.isOpen) {
      this.toggleDrawerClasses();
    }
  }

  toggleDrawerClasses() {
    this.onToggle();
    this.drawer.classList.toggle(this.classDrawerActive);
    this.body.classList.toggle(this.activeOverlayBodyClass);

    if (this.isOpen) {
      document
        .querySelector("cart-drawer-items")
        ?.scheduleHealPackLineQuantities?.();
    }

    // dispatch a custom event on the document
    const eventName = this.isOpen
      ? PUB_SUB_EVENTS.cartDrawerOpen
      : PUB_SUB_EVENTS.cartDrawerClose;

    document.dispatchEvent(
      new CustomEvent(eventName, {
        bubbles: true,
      }),
    );
  }

  init() {
    this.addEventListener("keydown", (e) => {
      const isTabPressed =
        e.key === "Tab" || e.keyCode === 9 || e.code === "Tab";
      const { first, last } = this.getFocusableElements();

      if (e.key === "Escape" || e.keyCode === 27 || e.code === "Escape") {
        if (this.isOpen) {
          this.toggleDrawerClasses();
        }
      }

      if (isTabPressed) {
        if (e.shiftKey && document.activeElement === first) {
          last.focus();
          e.preventDefault();
        } else if (!e.shiftKey && document.activeElement === last) {
          first.focus();
          e.preventDefault();
        }
      }
    });

    this.triggers().forEach((trigger) => {
      trigger.addEventListener("click", (e) => {
        e.preventDefault();
        this.toggleDrawerClasses();
      });
    });

    this.addEventListener("click", (e) => {
      if (e.target.classList.contains("wt-cart__drawer__close")) {
        e.preventDefault();
        this.toggleDrawerClasses();
      }
    });

  }

  openAfterAdd(parsedState) {
    const isClosedCart = !this.body.classList.contains(
      this.activeOverlayBodyClass,
    );
    const cartDrawerSectionHtml = parsedState?.sections?.["cart-drawer"];

    if (cartDrawerSectionHtml) {
      this.renderContents(parsedState, isClosedCart);
      return;
    }

    this.refreshCartDrawer({ detail: { openDrawer: true } });
  }

  syncEmptyStateClasses(itemCount) {
    if (typeof itemCount !== "number") return;

    const isEmpty = itemCount === 0;
    this.classList.toggle("wt-cart--empty", isEmpty);
    this.classList.toggle(
      "wt-cart--empty-upsell",
      isEmpty && this.dataset.emptyCartUpsell === "true",
    );
    this.classList.toggle("is-empty", isEmpty);
  }

  renderContents(parsedState, isClosedCart = true) {
    const cartDrawerHtml = parsedState?.sections?.["cart-drawer"];
    if (cartDrawerHtml) {
      const cartDrawer = document.querySelector("#CartDrawer");
      const drawerInnerHTML = this.getSectionInnerHTML(
        cartDrawerHtml,
        "#CartDrawer",
      );
      if (cartDrawer && drawerInnerHTML) {
        cartDrawer.innerHTML = drawerInnerHTML;
      }

      document
        .querySelector("cart-drawer-items")
        ?.scheduleHealPackLineQuantities?.();
    }

    const itemCount =
      parsedState?.item_count ??
      parsedState?.cartData?.item_count ??
      null;
    this.syncEmptyStateClasses(itemCount);

    this.updateCartIcon(parsedState);

    if (isClosedCart) {
      setTimeout(() => {
        this.ensureDrawerOpen();
        if (this.isOpen) {
          this.closeButton().focus();
        }
      });
    }
  }

  updateCartIcon(parsedState) {
    const iconSectionHtml = parsedState?.sections?.["cart-icon-bubble"];
    const pageHeaderCart = document.getElementById("cart-icon-bubble");

    if (pageHeaderCart && iconSectionHtml) {
      const iconInnerHTML = this.getSectionInnerHTML(
        iconSectionHtml,
        ".shopify-section",
      );
      if (iconInnerHTML) {
        pageHeaderCart.innerHTML = iconInnerHTML;
        return;
      }
    }

    const cartData =
      parsedState?.items != null
        ? parsedState
        : parsedState?.cartData?.items != null
          ? parsedState.cartData
          : null;
    const itemCount = this.resolveCartBadgeCount(cartData, parsedState);

    if (itemCount == null) return;

    document
      .querySelectorAll(".wt-cart__trigger")
      .forEach((trigger) => this.syncCartTriggerCount(trigger, itemCount));
  }

  resolveCartBadgeCount(cartData, parsedState) {
    if (cartData && window.WdCartPackCount?.getCartDisplayCount) {
      return window.WdCartPackCount.getCartDisplayCount(cartData);
    }

    const fallback =
      parsedState?.item_count ?? parsedState?.cartData?.item_count ?? null;

    return fallback;
  }

  syncCartTriggerCount(trigger, itemCount) {
    const badge = trigger.querySelector(
      ".wd-header__cart-badge, .wt-header__panel__counter",
    );

    trigger.setAttribute("data-cart-count", String(itemCount));

    if (itemCount > 0) {
      if (badge) {
        badge.textContent = itemCount;
        return;
      }

      const newBadge = document.createElement("span");
      newBadge.setAttribute("aria-hidden", "true");
      newBadge.setAttribute("data-cart-badge", "");
      newBadge.textContent = itemCount;
      newBadge.className = trigger.classList.contains("wd-header__cart-mobile")
        ? "wd-header__cart-badge"
        : trigger.classList.contains("wd-header__cart-btn")
          ? "wd-header__cart-badge"
          : "wt-header__panel__counter";
      trigger.appendChild(newBadge);
      return;
    }

    badge?.remove();
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    const node = new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector);
    return node?.innerHTML ?? "";
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  refreshCartDrawer(e) {
    const openDrawer = e?.detail?.openDrawer === true;
    const isClosedCart =
      openDrawer &&
      !this.body.classList.contains(this.activeOverlayBodyClass);
    const sectionsToRender = this.getSectionsToRender();
    fetch(
      `${window.Shopify.routes.root}?sections=${sectionsToRender[0].id},${sectionsToRender[1].id}`,
    )
      .then((response) => response.json())
      .then((response) => {
        const parsedState = {
          sections: response,
        };
        this.renderContents(parsedState, isClosedCart);
      })
      .catch((e) => {
        console.log(e);
      });
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("cart-drawer", CartDrawerSection);

const CART_ADD_SKIP_SOURCES = new Set([
  "cart-items",
  "cart-items-component",
  "cart-empty-upsell",
]);

let openCartDrawerTimer;

function openCartDrawerAfterAdd(response) {
  clearTimeout(openCartDrawerTimer);
  openCartDrawerTimer = setTimeout(() => {
    const cart = document.querySelector("cart-drawer");
    if (!cart || typeof cart.openAfterAdd !== "function") return false;
    cart.openAfterAdd(response || {});
    if (cart.classList.contains("is-empty")) {
      cart.classList.remove("is-empty");
    }
    return true;
  }, 0);
  return true;
}

window.openCartDrawerAfterAdd = openCartDrawerAfterAdd;

function shouldOpenDrawerForCartUpdate(detail) {
  const data = detail?.data;
  if (!data || data.didError) return false;
  if (data.source && CART_ADD_SKIP_SOURCES.has(data.source)) return false;
  return true;
}

function installCartDrawerAutoOpen() {
  if (window.__cartDrawerAutoOpenInstalled) return;
  window.__cartDrawerAutoOpenInstalled = true;

  document.addEventListener("cart:update", (event) => {
    if (!shouldOpenDrawerForCartUpdate(event.detail)) return;

    const sections =
      event.detail?.data?.sections || event.detail?.resource?.sections;
    openCartDrawerAfterAdd({
      sections,
      ...(event.detail?.resource || {}),
    });
  });

  if (typeof subscribe === "function") {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      const cartDrawer = document.querySelector("cart-drawer");
      const cartData =
        event?.cartData?.items != null ? event.cartData : null;
      const itemCount = cartDrawer?.resolveCartBadgeCount?.(
        cartData,
        event?.cartData || event,
      );

      if (typeof itemCount === "number") {
        document.querySelectorAll(".wt-cart__trigger").forEach((trigger) => {
          if (typeof cartDrawer?.syncCartTriggerCount === "function") {
            cartDrawer.syncCartTriggerCount(trigger, itemCount);
          }
        });
      }

      if (!event?.source || CART_ADD_SKIP_SOURCES.has(event.source)) return;
      openCartDrawerAfterAdd(event.cartData || {});
    });
  }

  const nativeFetch = window.fetch.bind(window);
  const cartAddPath = /\/cart\/add(\.js)?(\?|$)/i;

  function fixPackQuantityOnCartAdd(init) {
    const body = init?.body;
    if (!(body instanceof FormData)) return;

    const packUnits = parseInt(body.get("properties[_pack_units]"), 10);
    const quantity = parseInt(body.get("quantity"), 10);
    if (packUnits > 1 && quantity > 0 && quantity < packUnits) {
      body.set("quantity", String(quantity * packUnits));
    }
  }

  window.fetch = async function cartDrawerFetch(...args) {
    const request = args[0];
    const init = args[1] || {};
    const url =
      request instanceof Request
        ? new URL(request.url)
        : new URL(String(request), window.location.href);
    const method = (
      init.method || (request instanceof Request ? request.method : "GET")
    ).toUpperCase();

    if (method === "POST" && cartAddPath.test(url.pathname)) {
      fixPackQuantityOnCartAdd(init);
    }

    const response = await nativeFetch(...args);

    try {
      if (method !== "POST" || !cartAddPath.test(url.pathname) || !response.ok) {
        return response;
      }

      const data = await response.clone().json();
      if (data?.status) return response;

      openCartDrawerAfterAdd(data);
    } catch (error) {
      // Ignore non-JSON cart/add responses.
    }

    return response;
  };
}

installCartDrawerAutoOpen();

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    const sections = [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner",
      },
    ];

    if (document.getElementById("cart-icon-bubble")) {
      sections.push({
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      });
    }

    return sections;
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);
